import React, { Suspense, useContext, useState } from "react";
import FullLoader from "components/loader/full-loader";
import { Dropdown } from "react-bootstrap";
import { useDeleteData, useFetchData } from "hooks/useApiHooks";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { AppContext } from "contexts/AppContext";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import CreatePage from "./components/create-page";
import { useDeleteHandler } from "hooks/useDeleteHandler";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";

const SearchInput = ({ handleSearch, defaultValue }) => {
    const [search, setSearch] = useState(defaultValue || '');

    return (
        <div className="input-icon">
            <input type="text" className="form-control" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleSearch} />
            <span className="input-icon-addon">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon icon-tabler icon-tabler-search"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx="10" cy="10" r="7" />
                    <line x1="21" y1="21" x2="15" y2="15" />
                </svg>
            </span>
        </div>
    )
};

const TeamsPage = () => {
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const queryString = buildQueryString({ search: search || '', page: currentPage, limit: LIMIT_DATA_PER_PAGE });

    useFetchData(`/user${queryString}`, 'users');
    const { deleteData } = useDeleteData('/user', 'users', queryString);

    const { state } = useContext(AppContext);
    const { users } = state;

    const handleDelete = useDeleteHandler(deleteData, `User has been deleted`, `Failed to delete User`);
    const { handleSuccess, handleError, showCanvasHandler } = useCanvasHandler();

    const handleCreate = (e) => {
        e.preventDefault();
        showCanvasHandler('Create Team',
            <CreatePage
                onSuccess={handleSuccess}
                onError={handleError}
            />
        );
    };

    const handleUpdate = (e, data) => {
        e.preventDefault();
        showCanvasHandler('Update Team',
            <CreatePage
                initialData={data}
                onSuccess={handleSuccess}
                onError={handleError}
            />
        );
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            setSearch(e.target.value);
        }
    };

    const totalPages = users.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages,
        currentPage,
        onPageChange: setCurrentPage,
        visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
    });

    if (users.loading) return <FullLoader />;
    if (users.error) return <Suspense error={users.error} />;

    return (
        <LayoutDashboard>
            <div className="container-fluid mt-5">
                <div className="table-container vh-100">
                    <div className="search-container mb-3">
                        {/* Search input */}
                        <SearchInput handleSearch={handleSearch} defaultValue={search} />

                        {/* Create button */}
                        <button className="btn btn-primary" onClick={handleCreate}>Create</button>
                    </div>

                    {/* Table */}
                    <div className="table">
                        <table className="table table-hover table-vcenter">
                            <thead>
                                <tr>
                                    <th>No</th>
                                    <th>Name</th>
                                    <th>Username</th>
                                    <th>Email</th>
                                    <th>Whatsapp</th>
                                    <th>Role</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            {users.data.length > 0 && (
                                <tbody>
                                    {users.data.map((data, index) => {
                                        return (
                                            <React.Fragment key={index}>
                                                <tr>
                                                    <td>
                                                        {index + 1}
                                                    </td>
                                                    <td>{data.name}</td>
                                                    <td>{data.username}</td>
                                                    <td>{data.email}</td>
                                                    <td>{data.whatsapp}</td>
                                                    <td>
                                                        {data.roles.map((role, index) => {
                                                            return (
                                                                <span key={index} className="badge bg-primary text-white me-1">{role}</span>
                                                            )
                                                        })}
                                                    </td>
                                                    <td className="text-end">
                                                        <Dropdown>
                                                            <Dropdown.Toggle variant="default" id="dropdownMenuButton1">
                                                                Actions
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item href="#" onClick={(e) => handleUpdate(e, data)}>Edit</Dropdown.Item>
                                                                <Dropdown.Item href="#" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(data.id);
                                                                }}>
                                                                    Remove
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        )
                                    })}
                                </tbody>
                            )}
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="mt-4">
                        {Pagination}
                    </div>
                </div>
            </div>
        </LayoutDashboard>
    );
};

export default TeamsPage;